/**
 * Formatting helpers for the analytics UI: compact numbers, range wording,
 * and human-readable event descriptions derived from event metadata.
 */

import { AnalyticsEvent, AnalyticsRange, EventCategory, EVENT_CATEGORY_TYPES } from '../api/analyticsApi';

export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return value.toLocaleString();
}

export const RANGE_WORD: Record<AnalyticsRange, string> = {
  today: 'today',
  '7d': 'last 7 days',
  '14d': 'last 14 days',
  '30d': 'last 30 days',
  all: 'all time',
};

export const DELTA_LABEL: Record<AnalyticsRange, string | null> = {
  today: 'vs yesterday',
  '7d': 'vs prior 7 days',
  '14d': 'vs prior 14 days',
  '30d': 'vs prior 30 days',
  all: null,
};

export const RANGE_OPTIONS: { key: AnalyticsRange; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7d' },
  { key: '14d', label: '14d' },
  { key: '30d', label: '30d' },
  { key: 'all', label: 'All time' },
];

/** Followers-card delta caption: growth INSIDE the window (today's count
 * vs the count at the window start), not "vs the prior window" like the
 * other cards — all-time = growth since we first measured. */
export const FOLLOWERS_DELTA_LABEL: Record<AnalyticsRange, string> = {
  today: 'today',
  '7d': 'last 7 days',
  '14d': 'last 14 days',
  '30d': 'last 30 days',
  all: 'since first measured',
};

export const EVENT_CATEGORY_OPTIONS: { key: EventCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'publishing', label: 'Publishing' },
  { key: 'tending', label: 'Tending' },
  { key: 'edits', label: 'Edits' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'agent', label: 'Agent' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'config', label: 'Config' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'system', label: 'System' },
];

export function categoryTypes(category: EventCategory) {
  return category === 'all' ? undefined : EVENT_CATEGORY_TYPES[category];
}

/** Tailwind text color for an event type in the feed. */
export function eventTypeColor(eventType: string): string {
  if (eventType.startsWith('agent.')) return 'text-violet-300';
  if (eventType === 'post.published' || eventType === 'social.linked') return 'text-emerald-400';
  if (eventType === 'post.failed' || eventType === 'social.unlinked' || eventType === 'social.relink_required') return 'text-red-400';
  if (eventType === 'post.scheduled' || eventType === 'post.deleted' || eventType === 'post.held') return 'text-amber-400';
  if (eventType === 'post.queued' || eventType === 'post.reordered') return 'text-sky-300';
  if (eventType === 'take.requested') return 'text-purple-300';
  if (eventType === 'video.generated') return 'text-purple-400';
  if (eventType === 'tend.edited') return 'text-slate-400';
  if (eventType === 'item.feedback') return 'text-teal-300';
  if (eventType.startsWith('analytics.')) return 'text-purple-300';
  if (eventType.startsWith('config.') || eventType === 'brand.settings_changed' || eventType === 'brand.logo_updated') return 'text-amber-300';
  if (eventType.startsWith('feed.')) return 'text-blue-300';
  if (eventType === 'brand.created' || eventType === 'brand.restored') return 'text-emerald-400';
  if (eventType === 'brand.archived') return 'text-amber-400';
  if (eventType === 'brand.deleted') return 'text-red-400';
  if (eventType.startsWith('tend.') || eventType === 'content.imported') return 'text-blue-300';
  if (eventType.startsWith('user.')) return 'text-sky-400';
  return 'text-gray-400';
}

function browserFromUserAgent(userAgent?: string | null): string | null {
  if (!userAgent) return null;
  if (/mobile|iphone|android/i.test(userAgent)) return 'Mobile';
  if (/safari/i.test(userAgent) && !/chrome|chromium/i.test(userAgent)) return 'Safari';
  if (/edg/i.test(userAgent)) return 'Edge';
  if (/firefox/i.test(userAgent)) return 'Firefox';
  if (/chrome|chromium/i.test(userAgent)) return 'Chrome';
  return 'Web';
}

/** One-line human detail for the events table, derived from metadata. */
export function describeEvent(event: AnalyticsEvent): string {
  const meta = event.metadata || {};
  const title = meta.title ? `“${String(meta.title).slice(0, 80)}”` : '';
  const platforms = Array.isArray(meta.platforms) ? meta.platforms.join(', ') : meta.platform;

  switch (event.event_type) {
    case 'agent.run':
      return [platforms, meta.tactic_id, title].filter(Boolean).join(' · ');
    case 'agent.burst':
      return meta.title || `${meta.runs_last_hour} runs in the last hour (threshold ${meta.threshold})`;
    case 'agent.request':
      return [meta.action ? `asks: ${meta.action}` : null, title, meta.note ? String(meta.note).slice(0, 80) : null]
        .filter(Boolean).join(' · ');
    case 'post.published':
      return [platforms, title, meta.partial ? 'partial' : null].filter(Boolean).join(' · ');
    case 'post.failed':
      return [platforms, meta.error_message].filter(Boolean).join(' · ') || 'publish failed';
    case 'post.scheduled':
      return [platforms, meta.scheduled_for ? `for ${new Date(meta.scheduled_for).toLocaleString()}` : null]
        .filter(Boolean).join(' · ');
    case 'post.deleted':
      return 'post removed from platforms';
    // The queue doors (Channel Queue manual ordering, 2026-10-27): which
    // post, which channel, and what happened to its place in line.
    case 'post.queued':
      return [platforms, `${meta.requeued ? 'moved' : 'queued'} to ${meta.placement || 'back'}`, title]
        .filter(Boolean).join(' · ');
    case 'post.reordered':
      return [
        platforms,
        meta.whole_line ? 'line reordered' : null,
        meta.from_rank && meta.to_rank ? `#${meta.from_rank} → #${meta.to_rank}` : 'moved',
        title,
      ].filter(Boolean).join(' · ');
    case 'post.held':
      return [
        platforms,
        meta.limit ? `over the ${meta.limit}-character limit${meta.length ? ` (${meta.length})` : ''}`
          : meta.reason ? String(meta.reason).replace(/_/g, ' ') : 'held',
        title,
      ].filter(Boolean).join(' · ');
    case 'take.requested':
      return [platforms, meta.detail || (meta.source === 'tactic' ? 'by tactic' : 'take requested'), title]
        .filter(Boolean).join(' · ');
    case 'analytics.insights_run':
      return [
        meta.range_label,
        meta.model_name ? `via ${meta.model_name}` : null,
        meta.one_off ? 'one-off model test' : null,
      ].filter(Boolean).join(' · ') || 'editorial insights generated';
    case 'tend.created':
      return [meta.platform, meta.tactic_id, title].filter(Boolean).join(' · ');
    case 'content.imported':
      return [title || meta.url, meta.via ? `via ${String(meta.via).replace('_', ' ')}` : null]
        .filter(Boolean).join(' · ');
    case 'video.generated':
      return [meta.platform, meta.render_mode, meta.duration_seconds ? `${Math.round(meta.duration_seconds)}s` : null]
        .filter(Boolean).join(' · ');
    case 'user.login':
      return [browserFromUserAgent(meta.user_agent), meta.client_ip].filter(Boolean).join(' · ');
    case 'user.registered':
      return 'new account created';
    case 'user.added_to_brand': {
      const who = meta.user_name || meta.email || 'user';
      return [`${who} added`, meta.role ? `as ${meta.role}` : null].filter(Boolean).join(' ');
    }
    case 'user.role_changed': {
      const who = meta.user_name || meta.email || 'user';
      return meta.new_role ? `${who} → ${meta.new_role}` : `${who}'s role changed`;
    }
    case 'user.removed_from_brand':
      return `${meta.user_name || meta.email || 'user'} removed from brand`;
    case 'social.linked':
      return [meta.platform, meta.username, 'connected'].filter(Boolean).join(' · ');
    case 'social.unlinked':
      return [meta.platform, meta.source === 'system' ? 'unlinked by platform' : 'disconnected']
        .filter(Boolean).join(' · ');
    case 'social.relink_required':
      return `${meta.platform || 'account'} needs re-linking for analytics`;
    case 'tend.edited': {
      const fields: string[] = Array.isArray(meta.fields) ? meta.fields : [];
      const fieldText = fields.length
        ? fields.slice(0, 4).join(', ').replace(/_/g, ' ') + (fields.length > 4 ? ` +${fields.length - 4}` : '')
        : 'edited';
      return [meta.surface && meta.surface !== 'content' ? meta.surface : null, fieldText, title]
        .filter(Boolean).join(' · ');
    }
    case 'config.guidelines_updated':
      return `${meta.scope || 'brand'} guidelines updated`;
    case 'config.strategy_updated':
      return [meta.platform, 'strategy', meta.scope === 'universal' ? 'universal' : null].filter(Boolean).join(' · ');
    case 'config.strategy_reverted':
      return [meta.platform, 'strategy reverted to universal'].filter(Boolean).join(' · ');
    case 'config.tactic_created':
    case 'config.tactic_updated':
    case 'config.tactic_deleted':
      return [meta.name || meta.tactic_id, meta.platform, meta.scope === 'universal' ? 'universal' : null]
        .filter(Boolean).join(' · ');
    case 'config.tactic_toggled':
      return [`${meta.tactic_id} ${meta.enabled ? 'enabled' : 'disabled'}`, meta.platform].filter(Boolean).join(' · ');
    case 'config.prompt_changed':
      return [meta.action, meta.name ? `“${meta.name}”` : null, meta.position ? `(${meta.position})` : null,
        meta.model ? `model ${meta.model}` : null].filter(Boolean).join(' ');
    case 'config.template_changed':
      return [String(meta.action || 'changed').replace(/_/g, ' '),
        meta.name || (meta.template_id ? `template ${meta.template_id}` : null),
        meta.content_type].filter(Boolean).join(' · ');
    case 'config.font_changed':
      return [String(meta.action || 'changed').replace(/_/g, ' '), meta.family_name,
        meta.scope === 'universal' ? 'universal' : null].filter(Boolean).join(' · ');
    case 'feed.added':
      return [meta.source_name, meta.url].filter(Boolean).join(' · ') || 'feed added';
    case 'feed.toggled':
      return `${meta.source_name || 'feed'} ${meta.enabled === false ? 'disabled' : 'enabled'}`;
    case 'feed.removed':
      return `${meta.source_name || 'feed'} removed`;
    case 'brand.created':
      return [meta.name, meta.slug ? `(${meta.slug})` : null].filter(Boolean).join(' ');
    case 'brand.archived':
    case 'brand.restored':
    case 'brand.deleted':
      return meta.name || meta.slug || '';
    case 'brand.settings_changed':
      if (meta.setting === 'color_palette') return `color palette updated (${meta.count ?? 0} colors)`;
      if (meta.setting === 'background_color')
        return meta.color ? `page background set to ${meta.color}` : 'page background reset to sky';
      return `${String(meta.setting || 'setting').replace(/_/g, ' ')} ${meta.enabled ? 'on' : 'off'}`;
    case 'brand.logo_updated':
      return [meta.action === 'default_set' ? 'default set' : 'uploaded', meta.logo_key || meta.display_name]
        .filter(Boolean).join(' · ');
    default:
      return '';
  }
}

/** Compact label for pulse buckets: hour for today, day otherwise. */
export function pulseBucketLabel(bucket: string): string {
  if (bucket.includes('T')) {
    const hour = parseInt(bucket.split('T')[1], 10);
    if (Number.isNaN(hour)) return bucket;
    if (hour === 0) return '12a';
    if (hour < 12) return `${hour}a`;
    if (hour === 12) return '12p';
    return `${hour - 12}p`;
  }
  if (/^\d{4}-W?\d+/.test(bucket) && bucket.includes('W')) return bucket.split('-')[1];
  const date = new Date(`${bucket}T00:00:00`);
  return Number.isNaN(date.getTime()) ? bucket : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** The client an agent-originated event came through (the MCP principal's
 *  metadata: `client` is the connector's name, `api_key_name` the key). */
export function agentClient(event: AnalyticsEvent): string | null {
  if (event.actor_type !== 'agent') return null;
  const meta = event.metadata || {};
  return (meta.client && String(meta.client)) || (meta.api_key_name && String(meta.api_key_name)) || 'agent';
}
